package br.com.vvrefeicoes.portal.core.ui.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.FilterChip
import androidx.compose.material3.Icon
import androidx.compose.material3.InputChip
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Search
import com.composables.icons.lucide.X

data class SelectOption(
    val id: String,
    val label: String,
)

/**
 * Seleção múltipla: os itens escolhidos viram tags removíveis acima; os demais aparecem
 * horizontalmente abaixo (com busca). Sem busca, mostra no máximo [maxWhenEmpty] itens e um
 * indicador "(e +x)" para o restante.
 */
@Composable
fun MultiSelectField(
    options: List<SelectOption>,
    selectedIds: Set<String>,
    onToggle: (String) -> Unit,
    modifier: Modifier = Modifier,
    searchPlaceholder: String = "Buscar",
    emptyLabel: String = "Nenhuma opção disponível.",
    maxWhenEmpty: Int = 20,
    moreItemsLabel: @Composable (Int) -> String = { count -> "+$count" },
) {
    var query by remember { mutableStateOf("") }
    val selected = options.filter { it.id in selectedIds }
    val candidates = options.filter { it.id !in selectedIds && it.label.contains(query.trim(), ignoreCase = true) }
    val visible = if (query.isBlank()) candidates.take(maxWhenEmpty) else candidates
    val hidden = candidates.size - visible.size
    val hasUnselected = options.any { it.id !in selectedIds }

    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
        if (options.isEmpty()) {
            Text(
                text = emptyLabel,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            return@Column
        }

        if (selected.isNotEmpty()) {
            FlowRow(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
                verticalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                selected.forEach { option ->
                    InputChip(
                        selected = true,
                        onClick = { onToggle(option.id) },
                        label = { Text(option.label) },
                        trailingIcon = {
                            Icon(
                                imageVector = Lucide.X,
                                contentDescription = null,
                                modifier = Modifier.size(16.dp),
                            )
                        },
                    )
                }
            }
        }

        AppTextField(
            value = query,
            onValueChange = { query = it },
            placeholder = searchPlaceholder,
            leadingIcon = { Icon(imageVector = Lucide.Search, contentDescription = null) },
            modifier = Modifier.fillMaxWidth(),
        )

        FlowRow(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space8),
        ) {
            visible.forEach { option ->
                FilterChip(
                    selected = false,
                    onClick = { onToggle(option.id) },
                    label = { Text(option.label) },
                )
            }
            if (hidden > 0) {
                Text(
                    text = moreItemsLabel(hidden),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(vertical = AppSpacing.space8),
                )
            }
            if (visible.isEmpty() && hidden == 0 && hasUnselected) {
                Text(
                    text = emptyLabel,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(vertical = AppSpacing.space8),
                )
            }
        }
    }
}
